import React, { useState, useEffect } from 'react';
import { IMAGES, Icons } from '../constants';
import StressCheck from '../components/StressCheck';
import { CONTENT } from '../content';
import { db } from '../lib/firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import { Review } from '../types';

const StressBurnout: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const querySnapshot = await getDocs(query(collection(db, 'reviews'), orderBy('createdAt', 'desc')));
        const loaded: Review[] = [];
        querySnapshot.forEach((doc) => {
          const d = doc.data();
          loaded.push({
            id: doc.id,
            author: d.author || '',
            quote: d.quote || '',
            location: d.location || '',
            rating: d.rating || 5
          });
        });
        setReviews(loaded);
      } catch (err) {
        console.error("Error loading reviews:", err);
      }
    };
    fetchReviews();
  }, []);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const packages = [
    {
      name: "Kennismaking",
      price: "Gratis",
      duration: "Vrijblijvend inplannen",
      description: "Een eerste ontmoeting om te kijken of er een klik is.",
      features: [
        "Gesprek van 30 minuten",
        "In de praktijk of telefonisch",
        "Geen verdere verplichtingen",
        "Direct advies over vervolg"
      ],
      highlight: false,
      buttonText: "Nu Inplannen",
      vatLabel: ""
    },
    {
      name: "Stress & Burn-out Traject",
      price: "€ 1.950,-",
      duration: "Looptijd 3 maanden",
      description: "Voor stressproblematiek waarbij je nog volledig of gedeeltelijk aan het werk bent.",
      features: [
        "Programma van 4 modules (maatwerk)",
        "6-8 gesprekken",
        "Inclusief huiswerkopdrachten",
        "Tussendoor telefonisch & mail contact",
        "Afsluitende evaluatie"
      ],
      highlight: false,
      buttonText: "Start Traject",
      vatLabel: "Excl. BTW"
    },
    {
      name: "Stress & Burn-out Traject",
      price: "€ 3.550,-",
      duration: "Looptijd 6 maanden",
      description: "Intensief traject bij herstel en re-integratie van burn-out klachten.",
      features: [
        "Volledig programma (8 modules)",
        "12-14 gesprekken",
        "Inclusief huiswerkopdrachten",
        "Tussendoor telefonisch & mail contact",
        "Afsluitende evaluatie"
      ],
      highlight: false,
      buttonText: "Start Traject",
      vatLabel: "Excl. BTW"
    },
    {
      name: "Particulier Traject",
      price: "€ 1.950,-",
      duration: "Looptijd 3 maanden",
      description: "Hetzelfde intensieve traject van 3 maanden, speciaal voor particulieren.",
      features: [
        "Programma van 4 modules (maatwerk)",
        "6-8 gesprekken",
        "Inclusief huiswerkopdrachten",
        "Tussendoor telefonisch & mail contact",
        "Afsluitende evaluatie"
      ],
      highlight: false,
      buttonText: "Start Traject",
      vatLabel: "Incl. BTW"
    }
  ];

  return (
    <div className="pt-32 pb-16 bg-white relative overflow-hidden">
      {/* Speelse achtergrond elementen */}
      <div className="absolute top-40 left-[-5%] w-64 h-64 bg-accent/10 speels-blob animate-pulse"></div>
      <div className="absolute bottom-20 right-[-5%] w-96 h-96 bg-primary/5 speels-blob"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-stretch mb-24">
          {/* Linkerkolom */}
          <div className="w-full lg:w-1/2 flex flex-col">
            <div className="mb-12">
              <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-6 block">{CONTENT.stressBurnout.badge}</span>
              <h2 className="text-4xl md:text-6xl font-serif text-primary mb-10 leading-tight" dangerouslySetInnerHTML={{ __html: CONTENT.stressBurnout.title }}></h2>
              <p className="text-gray-600 text-lg font-light leading-relaxed">
                {CONTENT.stressBurnout.description}
              </p>
            </div>
            
            <div className="mt-auto flex items-center space-x-6 p-10 bg-liza-bg rounded-[2.5rem] border border-accent-light/60 shadow-sm relative overflow-hidden group hover:shadow-md transition-shadow duration-500">
              <div className="absolute top-0 right-0 p-4 opacity-5 text-primary group-hover:opacity-10 transition-opacity">
                 <Icons.Quote />
              </div>
              <div className="w-14 h-14 bg-white rounded-2xl flex-shrink-0 flex items-center justify-center text-primary shadow-sm">
                <Icons.Quote />
              </div>
              <p className="text-base text-gray-500 italic leading-relaxed font-light">
                {CONTENT.stressBurnout.quote}
              </p>
            </div>
          </div>
          
          {/* Rechterkolom met grid */}
          <div className="w-full lg:w-1/2">
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 h-full">
                {CONTENT.stressBurnout.stats.map((stat, i) => (
                  <div key={i} className="bg-liza-bg p-10 rounded-[2.5rem] border border-accent-light/50 hover:border-primary/30 transition-all duration-500 group shadow-sm hover:shadow-xl flex flex-col justify-center">
                    <span className="block text-primary font-serif text-2xl mb-3 group-hover:scale-105 transition-transform duration-500">{stat.count}</span>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{stat.label}</span>
                  </div>
                ))}
             </div>
          </div>
        </div>

        {/* Waarom kiezen sectie */}
        <div className="bg-primary rounded-[3rem] p-12 lg:p-20 text-white relative overflow-hidden shadow-2xl mb-24">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 speels-blob -mr-20 -mt-20"></div>
          <div className="relative z-10">
            <h3 className="text-3xl font-serif mb-12 text-center italic">{CONTENT.stressBurnout.whyTitle}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {CONTENT.stressBurnout.whyItems.map((item, idx) => (
                <div key={idx} className="space-y-4">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">{idx + 1}</div>
                  <h4 className="text-xl font-serif">{item.title}</h4>
                  <p className="text-white/70 font-light text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* DYNAMIC REVIEWS SECTION (BEIGE ACCENT BACKGROUND) */}
      {reviews.length > 0 && (
        <div className="bg-liza-bg py-24 mt-24 mb-0 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 speels-blob -mr-20 -mt-20"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="text-center mb-12">
              <span className="text-accent text-[10px] uppercase tracking-widest font-bold mb-3 block">Ervaringen</span>
              <h3 className="text-3xl font-serif text-primary italic">Resultaten van coachees</h3>
              <p className="text-gray-500 text-sm font-light mt-4 max-w-2xl mx-auto leading-relaxed">
                Lees hoe anderen hun coachingtraject bij Liza hebben ervaren en welke stappen zij hebben gezet in hun herstel.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {reviews.map((rev) => (
                <div key={rev.id} className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-accent-light/60 flex flex-col justify-between hover:shadow-lg transition-all duration-300">
                  <div>
                    {/* Stars */}
                    <div className="flex text-amber-400 mb-4 gap-0.5">
                      {Array.from({ length: rev.rating || 5 }).map((_, i) => (
                        <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    {/* Quote */}
                    <p className="text-gray-600 font-light text-sm italic leading-relaxed mb-6">
                      "{rev.quote}"
                    </p>
                  </div>
                  {/* Author metadata */}
                  <div className="border-t border-accent/10 pt-4 flex items-center justify-between">
                    <span className="text-xs font-bold text-primary font-serif italic">{rev.author}</span>
                    <span className="text-[10px] text-gray-400 font-mono">{rev.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* STRESS CHECK SECTIE */}
      <div id="stress-check" className="relative pt-12 pb-32 mb-24 scroll-mt-32">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.wideBanner} 
            alt="Natuurlijke omgeving voor herstel in Oostvoorne" 
            className="w-full h-full object-cover"
            width="1920"
            height="600"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <span className="text-white/80 font-bold tracking-[0.4em] text-[10px] uppercase mb-4 block drop-shadow-sm">{CONTENT.stressBurnout.stressCheckBadge}</span>
            <h3 className="text-4xl font-serif text-white drop-shadow-lg italic">{CONTENT.stressBurnout.stressCheckTitle}</h3>
            <p className="text-white/70 mt-4 font-light max-w-xl mx-auto">{CONTENT.stressBurnout.stressCheckDesc}</p>
          </div>
          
          <div className="transition-all duration-700 hover:scale-[1.01]">
            <StressCheck />
          </div>
        </div>
      </div>

      {/* Pakketten sectie */}
      <div id="tarieven" className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 pb-12 scroll-mt-24">
        <div className="text-center mb-16">
          <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-4 block">{CONTENT.stressBurnout.tarievenBadge}</span>
          <h3 className="text-4xl font-serif text-primary">{CONTENT.stressBurnout.tarievenTitle}</h3>
          <p className="text-gray-400 text-sm font-light mt-4 max-w-2xl mx-auto">
            {CONTENT.stressBurnout.tarievenDesc}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, i) => (
            <div 
              key={i} 
              className="relative bg-white p-6 lg:p-5 xl:p-8 rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-200/50 transition-all duration-500 hover:-translate-y-2 flex flex-col"
            >
              <div className="mb-6 h-[80px] md:h-[80px] lg:h-[105px] xl:h-[80px] flex flex-col justify-between"> {/* Vaste hoogte voor de headersectie */}
                <h4 className="text-2xl font-serif text-primary mb-1 leading-tight">{pkg.name}</h4>
                <span className="text-[10px] uppercase tracking-widest font-bold text-accent block">
                  {pkg.duration}
                </span>
              </div>
              <p className="text-gray-400 text-xs font-light mb-4 leading-relaxed h-[65px] lg:h-[85px] xl:h-[65px]">{pkg.description}</p>
              
              <div className="mb-10 flex items-baseline gap-1 h-[36px]"> {/* Vaste hoogte voor prijssectie */}
                <span className="text-3xl font-serif text-primary">{pkg.price}</span>
                {pkg.vatLabel && (
                  <div className="ml-auto">
                    <span className="block text-[10px] text-gray-400 uppercase tracking-widest font-bold">{pkg.vatLabel}</span>
                  </div>
                )}
              </div>

              <ul className="space-y-4 mb-12 flex-grow">
                {pkg.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-3 text-[13px] text-gray-600 font-light">
                    <div className="mt-1 flex-shrink-0 text-accent">
                      <Icons.Check />
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>

              <a 
                href="#contact" 
                onClick={(e) => handleScroll(e, 'contact')}
                className="w-full py-5 rounded-full text-center text-[10px] font-bold uppercase tracking-[0.2em] transition-all duration-300 bg-liza-bg text-primary hover:bg-accent-light"
              >
                {pkg.buttonText}
              </a>
            </div>
          ))}
        </div>
        
        <div className="mt-20 p-12 bg-liza-bg rounded-[3rem] border border-accent-light/50 mb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h5 className="text-xl font-serif text-primary mb-4 italic">{CONTENT.stressBurnout.employerTitle}</h5>
              <p className="text-sm text-gray-500 font-light leading-relaxed">
                {CONTENT.stressBurnout.employerDesc}
              </p>
            </div>
            <div>
              <h5 className="text-xl font-serif text-primary mb-4 italic">{CONTENT.stressBurnout.extraTitle}</h5>
              <p className="text-sm text-gray-500 font-light leading-relaxed">
                {CONTENT.stressBurnout.extraDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StressBurnout;